
import { useTranslation } from "react-i18next";
import cn from "classnames";
import Skeleton from "react-loading-skeleton";

import { formatPhoneNumber } from "capitalrx-utilities";
import { Bold } from "capitalrx-components-2.0/lib/typography";

import { Body, PanelContainer, PharmacyMapCallout, AppleMap, MapLocation } from "@/basic/components";
import { useCheckCurrentScreen } from "@/basic/hooks";
import { PharmacyInfo } from "@/api/types";

import styles from "./styles.module.scss";

const DetailListItemSkeleton = () => {
  return <Skeleton className={styles["DetailListItemSkeleton"]} />;
};

export interface PharmacyDetailsType {
  pharmacy: PharmacyInfo;
}

export interface PharmacyDetailsContainerProps {
  isLoading: boolean;
  data: PharmacyInfo;
  pharmacyData: Array<PharmacyDetailsType>;
  center: {
    lat: number;
    lng: number;
  },
}

export const PharmacyDetailsContainer = ({
  isLoading,
  data,
  pharmacyData,
  center,
}: PharmacyDetailsContainerProps) => {
  const { t: translate } = useTranslation();
  const screen = useCheckCurrentScreen();
  const wideScreen = screen === "desktop" || screen === "tablet";

  const DetailsContentWrapperClass = cn(styles["DetailsContentWrapper"], { DetailsContentMobileWrapper: !wideScreen });
  const panelClass = cn(styles["DetailPanelWrapper"], { DetailPanelMobileWrapper: !wideScreen });
  const PanelItemTitleClass = cn(
    "PanelItemTitle",
    { "PanelItemTitleMobile": !wideScreen },
  );

  return (
    <PanelContainer>
      <div className={DetailsContentWrapperClass}>
        <div className={panelClass}>
          {/* Phone number */}
          <div className="PanelItem PanelBottomBorder">
            <Body className={PanelItemTitleClass}>
              <Bold>{translate("PHARMACY_DETAILS_CONTAINER.phone")}:</Bold>
            </Body>
            {isLoading ? <DetailListItemSkeleton /> : <a href={`tel: ${data?.phone}`}>{formatPhoneNumber(data?.phone)}</a>}
          </div>

          {/* Hours */}
          <div className="PanelItem PanelBottomBorder">
            <Body className={PanelItemTitleClass}>
              <Bold>{translate("PHARMACY_DETAILS_CONTAINER.hours")}:</Bold>
            </Body>
            <div>
              {isLoading ? (
                <DetailListItemSkeleton />
              ) : (
                data?.hours.map((val) => {
                  return <Body key={val}>{val}</Body>;
                })
              )}
            </div>
          </div>

          {/* Drive thru */}
          <div className="PanelItem">
            <Body className={PanelItemTitleClass}>
              <Bold>{translate("PHARMACY_DETAILS_CONTAINER.driveThru")}:</Bold>
            </Body>
            <Body>{isLoading ? <DetailListItemSkeleton /> : data?.services.drive_up_window_indicator}</Body>
          </div>

          {/* Flu Shot */}
          <div className="PanelItem ZeroTopPadding">
            <Body className={PanelItemTitleClass}>
              <Bold>{translate("PHARMACY_DETAILS_CONTAINER.fluShots")}:</Bold>
            </Body>
            <Body>{isLoading ? <DetailListItemSkeleton /> : data?.services.immunizations_provided_indicator}</Body>
          </div>

          {/* Languages */}
          {data?.languages.length ? (
            <div className="PanelItem ZeroTopPadding">
              <Body className={PanelItemTitleClass}>
                <Bold>{translate("PHARMACY_DETAILS_CONTAINER.languages")}:</Bold>
              </Body>
              <Body>
                {isLoading ? (
                  <DetailListItemSkeleton />
                ) : (
                  data?.languages.map((val) => {
                    return <Body key={val}>{val}</Body>;
                  })
                )}
              </Body>
            </div>
          ) : null}
        </div>

        {pharmacyData && (
          <AppleMap
            style={{ height: "400px" }}
            renderCalloutContent={(location) => (
              <PharmacyMapCallout
                distance={location.data?.distance}
                id={location.subtitle}
                data={location.data as PharmacyInfo} />
            )}
            center={{
              latitude: center.lat,
              longitude: center.lng,
            }}
            locations={pharmacyData?.map((item): MapLocation => ({
              latitude: parseFloat(item.pharmacy.latitude),
              longitude: parseFloat(item.pharmacy.longitude),
              title: item.pharmacy.name,
              data: item.pharmacy,
            }))}
          />
        )}
      </div>
    </PanelContainer>
  );
};
